import { useMemo, useState } from "react";
import { Link, Navigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  Layers3,
  Loader2,
  Pencil,
  Percent,
  Sparkles,
  Tag,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  CheckCircle2,
  ReceiptText,
  FileStack,
  Wrench,
} from "lucide-react";
import {
  useProposalTemplate,
  useUpdateTemplate,
} from "@/modules/proposal-builder/hooks/useProposalTemplates";
import { TemplateWizard } from "@/modules/proposal-builder/components/templates/TemplateWizard";
import type {
  BuilderMode,
  ClientType,
  FixedPagePolicy,
  ProposalTemplate,
} from "@/modules/proposal-builder/types";
import { cn } from "@/lib/utils";

type TemplateDetailProps = {
  id: string;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

function formatEnumLabel(value: string) {
  return value.replace(/_/g, " ");
}

const CLIENT_TYPE_CONFIG: Record<
  ClientType,
  { label: string; color: string }
> = {
  construction: {
    label: "Construction",
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
  },
  restaurant: {
    label: "Restaurant",
    color: "text-blue-300 bg-blue-500/10 border-blue-500/20",
  },
  commercial: {
    label: "Commercial",
    color: "text-purple-300 bg-purple-500/10 border-purple-500/20",
  },
  residential: {
    label: "Residential",
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
  },
  other: {
    label: "Other",
    color: "text-white/45 bg-white/5 border-white/10",
  },
};

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description:
      "This template is currently optimized for the simpler template-first experience.",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description:
      "This template is flagged for the richer page-builder direction and future layout control.",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-4 w-4" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description:
      "This template mainly controls its own included pages and page mix.",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description:
      "This template leans on fixed pages defined by Brand Setup.",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description:
      "This template mixes brand-defined fixed pages with template-controlled pages.",
  },
};

function getTemplateClientType(template: ProposalTemplate): ClientType {
  const raw =
    template.client_type ??
    (template.category as ClientType | null | undefined) ??
    "other";

  if (
    raw === "construction" ||
    raw === "restaurant" ||
    raw === "commercial" ||
    raw === "residential" ||
    raw === "other"
  ) {
    return raw;
  }

  return "other";
}

function getTemplateBuilderMode(template: ProposalTemplate): BuilderMode {
  return template.builder_mode === "drag_drop" ? "drag_drop" : "prebuilt";
}

function getTemplateFixedPagePolicy(template: ProposalTemplate): FixedPagePolicy {
  if (
    template.fixed_page_policy === "brand_defined" ||
    template.fixed_page_policy === "mixed"
  ) {
    return template.fixed_page_policy;
  }

  return "template_defined";
}

function getFallbackStaticPageCount(template: ProposalTemplate): number {
  const ids = template.default_static_page_ids ?? [];
  return Array.from(
    new Set(
      ids.filter(
        (pageId): pageId is string =>
          typeof pageId === "string" && pageId.trim().length > 0
      )
    )
  ).length;
}

export function TemplateDetail({ id }: TemplateDetailProps) {
  if (!isUuid(id)) {
    return <Navigate to="/proposal-builder/templates" replace />;
  }

  const { data: template, isLoading, isError, error } = useProposalTemplate(id);
  const updateTemplate = useUpdateTemplate();

  const [isEditing, setIsEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const services =
    template?.template_services?.length
      ? template.template_services
      : [];
  const staticPages =
    template?.template_static_pages?.length
      ? template.template_static_pages
      : [];
  const templatePages = template?.template_pages ?? [];

  const serviceCount =
    services.length > 0
      ? services.length
      : template?.default_line_items?.length ?? 0;

  const staticPageCount =
    staticPages.length > 0
      ? staticPages.length
      : template
        ? getFallbackStaticPageCount(template)
        : 0;

  const templatePageCount = templatePages.length;

  const taxRateLabel =
    typeof template?.tax_rate === "number"
      ? `${(template.tax_rate * 100).toFixed(2)}%`
      : typeof template?.default_tax_rate === "number"
        ? `${(template.default_tax_rate * 100).toFixed(2)}%`
        : "0.00%";

  const clientType = template ? getTemplateClientType(template) : "other";
  const clientCfg = CLIENT_TYPE_CONFIG[clientType];

  const builderMode = template ? getTemplateBuilderMode(template) : "prebuilt";
  const builderCfg = BUILDER_MODE_CONFIG[builderMode];

  const fixedPagePolicy = template
    ? getTemplateFixedPagePolicy(template)
    : "template_defined";
  const fixedCfg = FIXED_PAGE_POLICY_CONFIG[fixedPagePolicy];

  const summary = useMemo(() => {
    const sourceServices =
      services.length > 0 ? services : template?.template_services ?? [];

    const withPricing = sourceServices.filter(
      (service) => typeof service.base_price === "number"
    ).length;

    const withDescriptions = sourceServices.filter(
      (service) => !!service.service_description?.trim()
    ).length;

    const customServices = sourceServices.filter(
      (service) => !service.service_item_id
    ).length;

    const linkedCatalogServices = sourceServices.filter(
      (service) => !!service.service_item_id
    ).length;

    return {
      withPricing,
      withDescriptions,
      customServices,
      linkedCatalogServices,
      layoutVersion: template?.layout_version ?? 1,
      fixedPagePolicyLabel: fixedCfg.label,
      builderModeLabel: builderCfg.label,
    };
  }, [builderCfg.label, fixedCfg.label, services, template?.layout_version, template?.template_services]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0b0b0c] text-white">
        <Loader2 className="h-5 w-5 animate-spin text-white/40" />
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] px-6 py-10 text-white">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
          <Link
            to="/proposal-builder/templates"
            className="inline-flex w-fit items-center gap-2 text-sm text-white/50 transition hover:text-white/80"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to templates
          </Link>

          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
            <h1 className="text-2xl font-semibold">Template not found</h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-white/45">
              {error instanceof Error
                ? error.message
                : "This template could not be loaded."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="min-h-screen bg-[#0b0b0c] text-white">
        <div className="mx-auto w-full max-w-7xl px-6 py-8">
          {saveError ? (
            <div className="mb-6 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="text-sm text-red-300">{saveError}</p>
            </div>
          ) : null}

          <TemplateWizard
            mode="edit"
            initialTemplate={template}
            isSubmitting={updateTemplate.isPending}
            onCancel={() => {
              setSaveError(null);
              setIsEditing(false);
            }}
            onSubmit={async (payload) => {
              try {
                setSaveError(null);

                await updateTemplate.mutateAsync({
                  id: template.id,
                  updates: payload,
                });

                setIsEditing(false);
              } catch (err) {
                setSaveError(
                  err instanceof Error
                    ? err.message
                    : "Failed to update template."
                );
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0b0c] px-6 py-10 text-white">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8">
        <Link
          to="/proposal-builder/templates"
          className="inline-flex w-fit items-center gap-2 text-sm text-white/50 transition hover:text-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to templates
        </Link>

        <div className="overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04]">
          <div className="border-b border-white/10 bg-gradient-to-br from-white/[0.08] to-transparent px-8 py-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-cyan-200">
                    <Sparkles className="h-3.5 w-3.5" />
                    Proposal template
                  </div>

                  {template.is_default && (
                    <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-amber-300">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Default
                    </div>
                  )}
                </div>

                <div>
                  <h1 className="text-3xl font-semibold tracking-tight text-white">
                    {template.name}
                  </h1>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/55">
                    {template.description?.trim() ||
                      "No description added for this template yet."}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                      clientCfg.color
                    )}
                  >
                    <Tag className="h-3.5 w-3.5" />
                    {clientCfg.label}
                  </span>

                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                      builderCfg.color
                    )}
                  >
                    {builderCfg.icon}
                    {builderCfg.label}
                  </span>

                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                      fixedCfg.color
                    )}
                  >
                    {fixedCfg.icon}
                    {fixedCfg.label}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-4 lg:items-end">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSaveError(null);
                      setIsEditing(true);
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm font-medium text-cyan-100 transition hover:bg-cyan-400/20"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit template
                  </button>

                  <Link
                    to="/proposal-builder/new"
                    search={{ templateId: template.id }}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10"
                  >
                    <ArrowRight className="h-4 w-4" />
                    Use for proposal
                  </Link>
                </div>

                <div className="grid min-w-[280px] grid-cols-1 gap-3 sm:grid-cols-2 lg:w-[420px] lg:grid-cols-2">
                  <MetricCard
                    label="Services"
                    value={`${serviceCount}`}
                    icon={<Layers3 className="h-4 w-4" />}
                  />
                  <MetricCard
                    label="Static pages"
                    value={`${staticPageCount}`}
                    icon={<FileText className="h-4 w-4" />}
                  />
                  <MetricCard
                    label="Tax rate"
                    value={taxRateLabel}
                    icon={<Percent className="h-4 w-4" />}
                  />
                  <MetricCard
                    label="Layout version"
                    value={String(summary.layoutVersion)}
                    icon={<ReceiptText className="h-4 w-4" />}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 px-8 py-8 lg:grid-cols-[1.1fr_0.9fr]">
            <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
              <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                <Layers3 className="h-4 w-4 text-cyan-300" />
                Included services
              </div>

              {serviceCount === 0 ? (
                <p className="mt-5 text-sm text-white/40">
                  No services have been attached to this template yet.
                </p>
              ) : (
                <div className="mt-5 space-y-3">
                  {services.length > 0 ? (
                    services.map((service) => (
                      <div
                        key={service.id}
                        className="rounded-2xl border border-white/10 bg-black/20 p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-white/85">
                                {service.service_name}
                              </p>

                              {!service.service_item_id ? (
                                <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-white/40">
                                  Template-only
                                </span>
                              ) : null}
                            </div>

                            {service.service_description ? (
                              <p className="mt-2 text-sm leading-6 text-white/45">
                                {service.service_description}
                              </p>
                            ) : (
                              <p className="mt-2 text-sm text-white/28">
                                No default description saved for this service.
                              </p>
                            )}
                          </div>

                          <div className="shrink-0 text-right">
                            {typeof service.base_price === "number" ? (
                              <span className="block whitespace-nowrap text-sm font-medium text-white/70">
                                ${service.base_price.toFixed(2)}
                              </span>
                            ) : (
                              <span className="block whitespace-nowrap text-sm text-white/30">
                                No price
                              </span>
                            )}

                            <span className="mt-1 block text-xs text-white/30">
                              {service.price_unit ?? "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    (template.default_line_items ?? []).map((service, index) => (
                      <div
                        key={`${service.name}-${index}`}
                        className="rounded-2xl border border-white/10 bg-black/20 p-4"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-white/85">
                              {service.name}
                            </p>

                            {service.description ? (
                              <p className="mt-2 text-sm leading-6 text-white/45">
                                {service.description}
                              </p>
                            ) : (
                              <p className="mt-2 text-sm text-white/28">
                                No default description saved for this service.
                              </p>
                            )}
                          </div>

                          <div className="shrink-0 text-right">
                            {typeof service.price === "number" ? (
                              <span className="block whitespace-nowrap text-sm font-medium text-white/70">
                                ${service.price.toFixed(2)}
                              </span>
                            ) : (
                              <span className="block whitespace-nowrap text-sm text-white/30">
                                No price
                              </span>
                            )}

                            <span className="mt-1 block text-xs text-white/30">
                              {service.price_unit ?? "—"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </section>

            <div className="space-y-6">
              <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                  <Tag className="h-4 w-4 text-cyan-300" />
                  Template details
                </div>

                <div className="mt-5 space-y-4 text-sm">
                  <DetailRow label="Template name" value={template.name} />
                  <DetailRow label="Category" value={clientCfg.label} />
                  <DetailRow label="Builder mode" value={summary.builderModeLabel} />
                  <DetailRow
                    label="Fixed page policy"
                    value={summary.fixedPagePolicyLabel}
                  />
                  <DetailRow label="Tax rate" value={taxRateLabel} />
                  <DetailRow
                    label="Created"
                    value={formatDate(template.created_at)}
                  />
                  <DetailRow
                    label="Last updated"
                    value={formatDate(template.updated_at)}
                  />
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                  {builderCfg.icon}
                  Builder foundation
                </div>

                <div className="mt-5 space-y-4">
                  <InfoCallout text={builderCfg.description} />
                  <InfoCallout text={fixedCfg.description} />

                  <div className="grid gap-3 sm:grid-cols-2">
                    <MiniStat
                      label="Services with pricing"
                      value={String(summary.withPricing)}
                    />
                    <MiniStat
                      label="Services with descriptions"
                      value={String(summary.withDescriptions)}
                    />
                    <MiniStat
                      label="Catalog-linked services"
                      value={String(summary.linkedCatalogServices)}
                    />
                    <MiniStat
                      label="Template-only services"
                      value={String(summary.customServices)}
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                  <FileStack className="h-4 w-4 text-cyan-300" />
                  Page structure
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <MiniStat
                    label="Static pages"
                    value={String(staticPageCount)}
                  />
                  <MiniStat
                    label="Template pages"
                    value={String(templatePageCount)}
                  />
                  <MiniStat
                    label="Layout version"
                    value={String(summary.layoutVersion)}
                  />
                </div>

                {templatePageCount > 0 ? (
                  <div className="mt-5 space-y-3">
                    {templatePages.map((page) => (
                      <div
                        key={page.id}
                        className="rounded-2xl border border-white/10 bg-black/20 p-4"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-white/85">
                            {page.title}
                          </p>

                          <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-white/35">
                            {formatEnumLabel(page.page_type)}
                          </span>

                          {page.is_required ? (
                            <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-emerald-300">
                              Required
                            </span>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                  <FileText className="h-4 w-4 text-cyan-300" />
                  Included static pages
                </div>

                {staticPageCount === 0 ? (
                  <p className="mt-5 text-sm text-white/40">
                    No static pages are included in this template.
                  </p>
                ) : (
                  <div className="mt-5 space-y-3">
                    {staticPages.length > 0 ? (
                      staticPages.map((page) => (
                        <div
                          key={page.id}
                          className="rounded-2xl border border-white/10 bg-black/20 p-4"
                        >
                          <p className="text-sm font-semibold text-white/85">
                            {page.title}
                          </p>
                          {page.content_html ? (
                            <p className="mt-2 line-clamp-4 text-sm leading-6 text-white/45">
                              {page.content_html}
                            </p>
                          ) : (
                            <p className="mt-2 text-sm text-white/28">
                              No content saved for this page.
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      Array.from(
                        new Set(
                          (template.default_static_page_ids ?? []).filter(
                            (pageId): pageId is string =>
                              typeof pageId === "string" && pageId.trim().length > 0
                          )
                        )
                      ).map((pageId, index) => (
                        <div
                          key={`${pageId}-${index}`}
                          className="rounded-2xl border border-white/10 bg-black/20 p-4"
                        >
                          <p className="text-sm font-semibold text-white/85">
                            Static page linked
                          </p>
                          <p className="mt-2 break-all text-sm leading-6 text-white/45">
                            {pageId}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                  <Sparkles className="h-4 w-4 text-cyan-300" />
                  AI context
                </div>

                <div className="mt-5 space-y-4">
                  <DetailBlock
                    label="Tone"
                    value={template.ai_tone || "No tone set"}
                  />
                  <DetailBlock
                    label="Industry context"
                    value={
                      template.ai_industry_context ||
                      "No industry context added yet."
                    }
                  />
                  <DetailBlock
                    label="Service context"
                    value={
                      template.ai_service_context ||
                      "No service context added yet."
                    }
                  />
                </div>
              </section>

              <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6">
                <div className="flex items-center gap-2 text-sm font-medium text-white/75">
                  <Wrench className="h-4 w-4 text-cyan-300" />
                  Template handoff
                </div>

                <div className="mt-5 space-y-3">
                  <InfoCallout text="Brand Setup provides the company-wide foundation. This template chooses what gets reused by default." />
                  <InfoCallout text="When a new proposal starts from this template, the proposal wizard inherits these services, pages, builder settings, and AI context first." />
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/35">
        {icon}
        <span>{label}</span>
      </div>
      <p className="mt-3 text-lg font-semibold text-white/85">{value}</p>
    </div>
  );
}

function MiniStat({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
        {label}
      </p>
      <p className="mt-2 text-lg font-semibold text-white/85">{value}</p>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-white/6 pb-3 last:border-b-0 last:pb-0">
      <span className="text-white/40">{label}</span>
      <span className="text-right text-white/75">{value}</span>
    </div>
  );
}

function DetailBlock({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
        {label}
      </p>
      <p className="mt-2 text-sm leading-6 text-white/70">{value}</p>
    </div>
  );
}

function InfoCallout({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
      <p className="text-sm leading-6 text-white/55">{text}</p>
    </div>
  );
}

function formatDate(value: string | null | undefined) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}